// Agent identity details (rendered inside the PageHeader, beside the entity
// title) + the PageHeader-anchored Rename / Archive Popovers (agent.md).
// Rename and archive stay Popovers — quick single-purpose forms tied to
// their trigger (§4a); archive keeps the §2.12 archive-grade name-match
// confirm. Both are transient, deliberately NOT URL state.
import type { SyntheticEvent } from "react";
import { useRef, useState } from "react";

import type { components } from "../../api/schema";
import { requestErrorDescription } from "../../lib/errors";
import { formatDateTime } from "../../lib/format";
import {
  Button,
  Copyable,
  Inline,
  InlineError,
  Popover,
  Stack,
  Text,
  TextInput,
} from "../../ui";

type AgentResponse = components["schemas"]["AgentResponse"];

// One identity details line beside the entity title: ID chip + created/updated.
// Team ID is deliberately absent — the API addresses everything by resource
// ID; team never appears on the agent page.
export function AgentIdentityDetails({ agent }: { agent: AgentResponse }) {
  return (
    <>
      <Copyable label="Agent ID" value={agent.id} /> · Created{" "}
      {formatDateTime(agent.created_at)} · Updated{" "}
      {formatDateTime(agent.updated_at)}
    </>
  );
}

export interface AgentHeaderActionsProps {
  agent: AgentResponse;
  archiveError: string | null;
  isArchivePending: boolean;
  isRenamePending: boolean;
  onArchive: () => void;
  onCancelArchive: () => void;
  onCancelRename: () => void;
  onRename: (displayName: string) => Promise<void>;
  onStartArchive: () => void;
  onStartRename: () => void;
  showArchiveConfirm: boolean;
  showRenameForm: boolean;
}

// The ONLY chrome row's actions (§4b.1): Rename and Archive, each anchoring
// its plain Popover (the oversized `.marker-agent-popover` editor styling is
// retired with the editor Drawers).
export function AgentHeaderActions({
  agent,
  archiveError,
  isArchivePending,
  isRenamePending,
  onArchive,
  onCancelArchive,
  onCancelRename,
  onRename,
  onStartArchive,
  onStartRename,
  showArchiveConfirm,
  showRenameForm,
}: AgentHeaderActionsProps) {
  const archiveAnchorRef = useRef<HTMLButtonElement>(null);
  const renameAnchorRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <div className="marker-popover-anchor">
        <Button
          ref={renameAnchorRef}
          disabled={isArchivePending || isRenamePending}
          size="sm"
          variant="secondary"
          onClick={onStartRename}
        >
          Rename
        </Button>
        <Popover
          align="end"
          anchorRef={renameAnchorRef}
          aria-label="Rename agent"
          isOpen={showRenameForm}
          onDismiss={onCancelRename}
        >
          <AgentRenameForm
            agent={agent}
            isPending={isRenamePending}
            onCancel={onCancelRename}
            onRename={onRename}
          />
        </Popover>
      </div>
      <div className="marker-popover-anchor">
        <Button
          ref={archiveAnchorRef}
          disabled={isArchivePending || isRenamePending}
          size="sm"
          variant="secondary"
          onClick={onStartArchive}
        >
          Archive agent
        </Button>
        <Popover
          align="end"
          anchorRef={archiveAnchorRef}
          aria-label="Archive agent"
          isOpen={showArchiveConfirm}
          size="wide"
          onDismiss={onCancelArchive}
        >
          <ArchiveAgentConfirm
            agent={agent}
            error={archiveError}
            isPending={isArchivePending}
            onArchive={onArchive}
            onCancel={onCancelArchive}
          />
        </Popover>
      </div>
    </>
  );
}

interface AgentRenameFormProps {
  agent: AgentResponse;
  isPending: boolean;
  onCancel: () => void;
  onRename: (displayName: string) => Promise<void>;
}

export function AgentRenameForm(props: AgentRenameFormProps) {
  return <AgentRenameFormContent key={props.agent.id} {...props} />;
}

function AgentRenameFormContent({
  agent,
  isPending,
  onCancel,
  onRename,
}: AgentRenameFormProps) {
  const [displayName, setDisplayName] = useState(agent.display_name);
  const [localError, setLocalError] = useState<string | null>(null);
  const normalizedName = displayName.trim();
  // Save disabled when empty/unchanged (preserved behavior).
  const isDisabled =
    isPending ||
    normalizedName.length === 0 ||
    normalizedName === agent.display_name;

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isDisabled) {
      return;
    }
    setLocalError(null);
    try {
      await onRename(normalizedName);
    } catch (caught) {
      setLocalError(requestErrorDescription(caught));
    }
  }

  return (
    <Stack gap={3}>
      <Text variant="caps">Rename agent</Text>
      <form
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <Stack gap={3}>
          <TextInput
            label="Display name"
            required
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.currentTarget.value);
            }}
          />
          {localError ? <InlineError>{localError}</InlineError> : null}
          <Inline>
            <Button disabled={isDisabled} type="submit">
              {isPending ? "Saving..." : "Save"}
            </Button>
            <Button
              disabled={isPending}
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Inline>
        </Stack>
      </form>
    </Stack>
  );
}

interface ArchiveAgentConfirmProps {
  agent: AgentResponse;
  error: string | null;
  isPending: boolean;
  onArchive: () => void;
  onCancel: () => void;
}

// §2.12 archive-grade confirm: description sentence naming the target + the
// exact-display-name TextInput gating the danger button.
export function ArchiveAgentConfirm({
  agent,
  error,
  isPending,
  onArchive,
  onCancel,
}: ArchiveAgentConfirmProps) {
  const [confirmation, setConfirmation] = useState("");
  const canArchive = confirmation === agent.display_name && !isPending;

  return (
    <Stack gap={4}>
      <Text variant="caps">Archive agent</Text>
      <Stack gap={4}>
        <Text variant="muted">
          {`Archive ${agent.display_name}? Transcripts and Markers are kept.`}
        </Text>
        <TextInput
          label="Type the agent name to confirm"
          value={confirmation}
          onChange={(event) => {
            setConfirmation(event.currentTarget.value);
          }}
        />
        {error ? <InlineError>{error}</InlineError> : null}
        <Inline>
          <Button
            disabled={isPending}
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            disabled={!canArchive}
            type="button"
            variant="danger"
            onClick={onArchive}
          >
            {isPending ? "Archiving..." : "Archive agent"}
          </Button>
        </Inline>
      </Stack>
    </Stack>
  );
}
